/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import styled from "styled-components";

import { Block } from "src/classes/block";
import { Plate } from "src/classes/plate/Plate";
import { BlockTopbarButtons } from "src/components/Block/BlockTopbar/BlockTopbarButtons";
import { DeleteButton } from "src/components/Block/BlockTopbar/BlockTopbarButtons/DeleteButton";
import { EmptySpace, StyledBlockTopbar } from "src/components/Block/BlockTopbar/styles";
import { stringToHSL } from "src/lib/utils/color";

interface Props {
  plate: Plate;
  block: Block;
  hovering: boolean;
}

export function PlateBlockTopbar({ block, plate, hovering }: Props) {
  const editable = plate.observe((state) => state.editable);
  /* ------------------ Is Block Selected ------------------ */
  const selection = plate.observe((state) => state.selection);
  const isSelected = selection.includes(block.snapshot.id);

  /* ------------------ Is Block Locked ------------------ */
  const lockedBlocks = plate.socket.observe((state) => state.lockedBlocks);
  const isLocked = lockedBlocks.map(({ blockId: id }) => id).includes(block.snapshot.id);
  const lockingUser = lockedBlocks.find(({ blockId }) => blockId === block.snapshot.id)?.lockedBy;
  const tintColor = lockingUser ? stringToHSL(lockingUser, { lit: [40, 90], sat: [75, 100] }) : undefined;

  /* ------------------ For Button UI State ------------------ */
  const isCurrentSession = plate.sessionManager.observe((state) => state.isCurrentSession);

  return (
    <StyledBlockTopbar isSelected={isSelected} tintColor={tintColor} isSelectable={editable && !isLocked}>
      {lockingUser && <LockingUsername>@{lockingUser}</LockingUsername>}
      {editable && <DeleteButton deleteBlock={() => plate.deleteBlock(block.snapshot.id)} />}
      <EmptySpace
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => editable && plate.onDragBlockStart(block, e)}
        editable={!isLocked && editable}
      />
      <BlockTopbarButtons block={block} hovering={hovering} isCurrentSession={isCurrentSession} />
    </StyledBlockTopbar>
  );
}

const LockingUsername = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "IBM Plex Mono";
  font-size: 11px;
  color: ${({ theme }) => theme.text.subtle};
`;
